/*
 * Autorska prava pridržava Mali mol d.o.o. Pula
 * Copyright (c) 2016. by Mali mol d.o.o. Pula. All rights reserved.
 */

import { ref, onMounted, onUnmounted, type Ref } from 'vue';
import { useVersionCheck } from './useVersionCheck';

const DEFAULT_POLL_INTERVAL_MS = 60_000;

export function useWasmUpdatePoll(
    intervalMs: number = DEFAULT_POLL_INTERVAL_MS,
): { updateAvailable: Ref<boolean> } {
    const { hasNewWasm } = useVersionCheck();
    const updateAvailable = ref(false);
    let timer: ReturnType<typeof setInterval> | null = null;

    const stop = (): void => {
        if (timer !== null) {
            clearInterval(timer);
            timer = null;
        }
    };

    const check = async (): Promise<void> => {
        if (updateAvailable.value) return;
        if (await hasNewWasm()) {
            updateAvailable.value = true;
            stop();
        }
    };

    const onVisibility = (): void => {
        if (document.visibilityState === 'visible') void check();
    };

    onMounted(() => {
        void check();
        timer = setInterval(() => void check(), intervalMs);
        document.addEventListener('visibilitychange', onVisibility);
    });

    onUnmounted(() => {
        stop();
        document.removeEventListener('visibilitychange', onVisibility);
    });

    return { updateAvailable };
}
